import React, { memo } from 'react';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ProductPreview from './ProductPreview';

const DesignNotes = memo(({ side, value, onChange }) => {
  const handleChange = (e) => {
    onChange(side, e.target.value);
  };

  return (
    <textarea
      name={`notes-${side}`}
      id={`notes-${side}`}
      rows={3}
      value={value || ''}
      onChange={handleChange}
      placeholder={`Add any specific instructions or notes about the ${side} design, especially sizing and placement instructions...`}
      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-white shadow-sm focus:border-cyan-500 focus:ring-cyan-500 p-3"
    />
  );
});

DesignNotes.displayName = 'DesignNotes';

const ArtworkUpload = memo(({ side, files, onArtworkChange }) => {
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length > 0) {
      onArtworkChange(side, newFiles);
    }
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    onArtworkChange(side, newFiles);
  };

  return (
    <div>
      <div className="mt-3 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-md hover:border-cyan-500 transition-colors">
        <div className="space-y-1 text-center">
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="flex text-sm text-gray-400">
            <label htmlFor={`artwork-${side}`} className="relative cursor-pointer rounded-md font-medium text-cyan-500 hover:text-cyan-400">
              <span>Upload files</span>
              <input
                id={`artwork-${side}`}
                name={`artwork-${side}`}
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".ai,.eps,.pdf,.png,.jpg,.jpeg,.svg"
                className="sr-only"
              />
            </label>
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs text-gray-400">
            AI, EPS, PDF, PNG, JPG, SVG up to 10MB each
          </p>
        </div>
      </div>
      {files?.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-700 p-3 rounded-lg">
              <span className="text-sm text-gray-300">{file.file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="text-gray-400 hover:text-red-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

ArtworkUpload.displayName = 'ArtworkUpload';

const ArtworkSection = memo(({ side, formData, currentItem, onNotesChange, onArtworkChange, onSaveSnapshot }) => {
  const isHat = currentItem.type === 'Hats';
  const title = isHat ? 'Embroidery Design Notes' : `${side.charAt(0).toUpperCase() + side.slice(1)} Design Notes`;
  const uploadLabel = isHat ? 'Upload Embroidery Artwork' : `Upload ${side.charAt(0).toUpperCase() + side.slice(1)} Artwork`;

  return (
    <div className="space-y-6 bg-gray-800 p-4 rounded-lg">
      <div>
        <label htmlFor={`notes-${side}`} className="block text-sm font-medium text-gray-300">
          {title}
        </label>
        <DesignNotes
          side={side}
          value={formData.notes?.[side]}
          onChange={onNotesChange}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300">
          {uploadLabel}
        </label>
        <ArtworkUpload
          side={side}
          files={formData.artwork?.[side] || []}
          onArtworkChange={onArtworkChange}
        />
      </div>

      {!isHat && (
        <ProductPreview
          color={currentItem.color || '#FFFFFF'}
          artwork={formData.artwork?.[side]?.[0]?.preview}
          productType={currentItem.type}
          view={side}
          onSaveSnapshot={(snapshot) => onSaveSnapshot(side, snapshot)}
        />
      )}
    </div>
  );
});

ArtworkSection.displayName = 'ArtworkSection';

const DesignArtwork = ({ formData, currentItem, onNotesChange, onArtworkChange, onSaveSnapshot }) => {
  const isHat = currentItem.type === 'Hats';

  return (
    <div className="border-t border-gray-700 pt-8">
      <h3 className="text-lg font-medium text-magenta-500 mb-6">Design & Artwork</h3>
      
      {isHat ? (
        <div>
          <h4 className="text-md font-medium text-cyan-500 mb-4">Hat Embroidery Design</h4>
          <ArtworkSection
            side="front"
            formData={formData}
            currentItem={currentItem}
            onNotesChange={onNotesChange}
            onArtworkChange={onArtworkChange}
            onSaveSnapshot={onSaveSnapshot}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h4 className="text-md font-medium text-cyan-500 mb-4">Front Design</h4>
            <ArtworkSection
              side="front"
              formData={formData}
              currentItem={currentItem}
              onNotesChange={onNotesChange}
              onArtworkChange={onArtworkChange}
              onSaveSnapshot={onSaveSnapshot}
            />
          </div>

          <div>
            <h4 className="text-md font-medium text-cyan-500 mb-4">Back Design</h4>
            <ArtworkSection
              side="back"
              formData={formData}
              currentItem={currentItem}
              onNotesChange={onNotesChange}
              onArtworkChange={onArtworkChange}
              onSaveSnapshot={onSaveSnapshot}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DesignArtwork);